import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Timer, Pause } from "lucide-react";
import { useActiveTimer } from "@/hooks/useTimeEntries";
import { cn } from "@/lib/utils";

export function MiniTimer() {
  const { data: activeTimer } = useActiveTimer();
  const [seconds, setSeconds] = useState(0);

  // Check if timer is paused from database
  const isPaused = activeTimer?.is_paused === true;

  useEffect(() => {
    if (!activeTimer) {
      setSeconds(0);
      return;
    }

    const startTime = new Date(activeTimer.start_time).getTime();
    const pausedSeconds = activeTimer.paused_duration_seconds || 0;

    // If paused, calculate elapsed time up to now but don't keep updating
    if (isPaused) {
      const now = Date.now();
      const elapsed = Math.floor((now - startTime) / 1000) - pausedSeconds;
      setSeconds(Math.max(0, elapsed));
      return;
    }

    const updateTimer = () => {
      const now = Date.now();
      const elapsed = Math.floor((now - startTime) / 1000) - pausedSeconds;
      setSeconds(Math.max(0, elapsed));
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [activeTimer, isPaused]);

  // Esconde apenas se não houver timer ativo
  // Timer pausado continua visível para lembrar o usuário
  if (!activeTimer) return null;

  const formatTime = (totalSeconds: number) => {
    const hrs = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    }
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const projectName = activeTimer.projects?.name;

  return (
    <Link
      to="/timer"
      className={cn(
        "flex items-center gap-2 px-3 py-2 rounded-xl",
        isPaused 
          ? "bg-warning/10 hover:bg-warning/15 border border-warning/20"
          : "bg-primary/10 hover:bg-primary/15 border border-primary/20",
        "transition-all duration-200 ease-out",
        "group cursor-pointer"
      )}
    >
      <div className="relative">
        {isPaused ? (
          <Pause className="h-4 w-4 text-warning" />
        ) : (
          <>
            <Timer className="h-4 w-4 text-primary" />
            <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-success animate-pulse" />
          </>
        )}
      </div>
      
      <div className="flex flex-col">
        <span className={cn(
          "font-display text-sm tracking-wide font-medium",
          isPaused ? "text-warning" : "text-primary"
        )}>
          {formatTime(seconds)}
        </span>
        {projectName && (
          <span className="text-[10px] text-muted-foreground truncate max-w-[80px] leading-tight">
            {projectName}
          </span>
        )}
      </div>

      {isPaused && (
        <span className="text-[10px] font-medium text-warning uppercase tracking-wide">
          Pausado
        </span>
      )}
    </Link>
  );
}
